import React, { useState, useEffect } from 'react';
import { dashboardAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, AreaChart, Area, Legend,
} from 'recharts';
import { BarChart3, PieChartIcon, TrendingUp, Activity } from 'lucide-react';

const COLORS = ['#0f172a', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#6366f1'];
const STATUS_COLORS = {
  Approved: '#10b981', Submitted: '#0ea5e9', In_Progress: '#f59e0b',
  Carrier_Pending: '#8b5cf6', Missing_Information: '#ef4444', Declined: '#dc2626',
  Terminated: '#991b1b', On_Hold: '#6b7280', active: '#10b981', inactive: '#ef4444',
  onboarding: '#0ea5e9', prospect: '#a855f7', Open: '#0ea5e9', Closed: '#6b7280', Waiting_On_Agent: '#f59e0b',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white shadow-lg border rounded-lg px-3 py-2 text-sm">
      <p className="font-medium text-slate-900">{label || payload[0]?.name}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-slate-600">
          {p.dataKey || p.name}: <span className="font-semibold">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

const renderLabel = false; // Labels disabled — use tooltip + legend only

export function DashboardCharts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dashboardAPI.charts().then(res => setData(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {[1, 2, 3, 4].map(i => (
          <Card key={i} className="animate-pulse">
            <CardContent className="h-72 flex items-center justify-center">
              <div className="w-32 h-32 bg-slate-100 rounded-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!data) return null;

  const agentData = (data.agent_status || []).map(d => ({
    ...d, name: d.name.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()),
  }));
  const contractData = (data.contract_status || []).map(d => ({
    ...d, name: d.name.replace(/_/g, ' '),
  }));
  const monthlyData = data.monthly_requests || [];
  const carrierData = data.top_carriers || [];
  const activityData = data.daily_activity || [];
  const ticketData = (data.ticket_status || []).map(d => ({
    ...d, name: d.name.replace(/_/g, ' '),
  }));

  return (
    <div className="space-y-6 mb-8" data-testid="dashboard-charts">
      <div className="flex items-center gap-2 mb-2">
        <BarChart3 size={20} className="text-slate-600" />
        <h2 className="text-lg font-heading font-semibold text-slate-900">Analytics Overview</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Agent Status Distribution */}
        {agentData.length > 0 && (
          <Card className="animate-slideIn" data-testid="chart-agent-status">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading flex items-center gap-2">
                <PieChartIcon size={16} className="text-sky-500" />
                Agent Status Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={agentData} cx="50%" cy="50%" innerRadius={50} outerRadius={90}
                    paddingAngle={3} dataKey="value">
                    {agentData.map((entry, i) => (
                      <Cell key={i} fill={STATUS_COLORS[entry.name.toLowerCase()] || COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Contract Status Distribution */}
        {contractData.length > 0 && (
          <Card className="animate-slideIn" data-testid="chart-contract-status">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading flex items-center gap-2">
                <PieChartIcon size={16} className="text-emerald-500" />
                Contract Status Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={contractData} cx="50%" cy="50%" innerRadius={50} outerRadius={90}
                    paddingAngle={3} dataKey="value">
                    {contractData.map((entry, i) => (
                      <Cell key={i} fill={STATUS_COLORS[entry.name.replace(/ /g, '_')] || COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Monthly Contract Trends */}
        {monthlyData.length > 0 && (
          <Card className="animate-slideIn" data-testid="chart-monthly-trends">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading flex items-center gap-2">
                <TrendingUp size={16} className="text-violet-500" />
                Monthly Contracts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="requests" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Top Carriers */}
        {carrierData.length > 0 && (
          <Card className="animate-slideIn" data-testid="chart-top-carriers">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading flex items-center gap-2">
                <BarChart3 size={16} className="text-amber-500" />
                Top Carriers by Contracts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={carrierData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" allowDecimals={false} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} stroke="#94a3b8" width={100} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="contracts" fill="#0f172a" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Daily Activity Trend */}
        {activityData.length > 0 && (
          <Card className="animate-slideIn lg:col-span-2" data-testid="chart-activity-trend">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading flex items-center gap-2">
                <Activity size={16} className="text-rose-500" />
                Activity Trend (Last 30 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={activityData}>
                  <defs>
                    <linearGradient id="actGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="activities" stroke="#0ea5e9" strokeWidth={2} fill="url(#actGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
